import React from 'react';
import { Avatar, Box } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { formatKm, formatPace } from './contestFormat';
import { SuspiciousCountBadge } from './SuspiciousCountBadge';

export interface LeaderboardRow {
  key: string;
  rank: number;
  name: string;
  subtitle?: string;
  avatarUrl?: string;
  distance: number;
  count: number;
  averagePace: number;
  fastestPace: number;
  // Admin only: flagged activities awaiting review.
  suspiciousCount?: number;
  isCurrentUser?: boolean;
}

interface LeaderboardTableProps {
  rows: LeaderboardRow[];
  countLabel: string;
  onRowClick?: (row: LeaderboardRow) => void;
}

const COLUMNS = '48px minmax(160px,2fr) repeat(4,minmax(70px,1fr)) 20px';

export const MEDALS: Record<number, { bg: string; fg: string }> = {
  1: { bg: '#fde68a', fg: '#422006' },
  2: { bg: '#e4e4e7', fg: '#422006' },
  3: { bg: '#fed7aa', fg: '#422006' },
};

const headSx = { fontSize: 12, fontWeight: 500, color: 'var(--muted)' } as const;

// Shared ranked grid for team-member and individual leaderboards; scrolls sideways on narrow screens.
export const LeaderboardTable = ({ rows, countLabel, onRowClick }: LeaderboardTableProps) => {
  const { t } = useTranslation('contests');
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 560 }} role="table">
        <Box role="row" sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, p: '10px 16px', borderBottom: '1px solid var(--border)', ...headSx }}>
          <span>#</span>
          <span>{t('leaderboard.columns.athlete')}</span>
          <Box sx={{ textAlign: 'right' }}>{t('leaderboard.columns.distance')}</Box>
          <Box sx={{ textAlign: 'right' }}>{countLabel}</Box>
          <Box sx={{ textAlign: 'right' }}>{t('leaderboard.columns.avgPace')}</Box>
          <Box sx={{ textAlign: 'right' }}>{t('leaderboard.columns.fastest')}</Box>
          <span />
        </Box>
        {rows.map(row => {
          const medal = row.distance > 0 ? MEDALS[row.rank] : undefined;
          return (
            <Box
              key={row.key}
              role="row"
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              title={onRowClick ? t('leaderboard.viewActivities') : undefined}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLUMNS,
                gap: 1,
                p: '12px 16px',
                alignItems: 'center',
                fontSize: 14,
                borderBottom: '1px solid var(--border)',
                fontVariantNumeric: 'tabular-nums',
                cursor: onRowClick ? 'pointer' : undefined,
                backgroundColor: row.isCurrentUser ? 'var(--accent-bg)' : 'transparent',
                opacity: row.distance > 0 ? 1 : 0.7,
                '&:hover': { backgroundColor: onRowClick && !row.isCurrentUser ? 'var(--muted-bg)' : undefined },
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  backgroundColor: medal?.bg ?? 'transparent',
                  color: medal?.fg ?? 'var(--muted)',
                }}
              >
                {row.distance > 0 ? row.rank : '–'}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                {row.avatarUrl ? <Avatar src={row.avatarUrl} alt={row.name} sx={{ width: 30, height: 30 }} /> : <InitialsAvatar name={row.name} size={30} />}
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <Box component="span" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.name}
                    {row.isCurrentUser && <Box component="span" sx={{ ml: 0.75, fontSize: 12, color: 'var(--accent-fg)' }}>{t('leaderboard.you')}</Box>}
                    <SuspiciousCountBadge count={row.suspiciousCount} />
                  </Box>
                  {row.subtitle && (
                    <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.subtitle}
                    </Box>
                  )}
                </Box>
              </Box>
              <Box sx={{ textAlign: 'right', fontWeight: 600 }}>{formatKm(row.distance)}</Box>
              <Box sx={{ textAlign: 'right' }}>{row.count}</Box>
              <Box sx={{ textAlign: 'right' }}>{formatPace(row.averagePace)}</Box>
              <Box sx={{ textAlign: 'right', color: 'var(--muted)' }}>{formatPace(row.fastestPace)}</Box>
              <ChevronRightIcon sx={{ fontSize: 18, color: 'var(--muted)', visibility: onRowClick ? 'visible' : 'hidden' }} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
